import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { analytics } from '@/lib/analytics';
import { cn } from '@/lib/utils';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AuthLogo } from './AuthLogo';
import { InputWithError } from './InputWithError';

export const ResetPasswordForm = ({ className, ...props }: React.ComponentProps<'div'>) => {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const token = searchParams.get('token');

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validate: (values) => {
      const errors: { password?: string; confirmPassword?: string } = {};

      if (!values.password) {
        errors.password = t('resetPassword.passwordRequired');
      } else if (values.password.length < 8) {
        errors.password = t('resetPassword.passwordMinLength');
      }

      if (!values.confirmPassword) {
        errors.confirmPassword = t('resetPassword.confirmPasswordRequired');
      } else if (values.password !== values.confirmPassword) {
        errors.confirmPassword = t('resetPassword.passwordsNoMatch');
      }

      return errors;
    },
    onSubmit: async (values) => {
      if (!token) {
        toast.error(t('resetPassword.invalidResetLink'), {
          description: t('resetPassword.invalidResetLinkDescription'),
        });
        return;
      }

      setIsLoading(true);

      const toastId = toast.loading(t('resetPassword.resettingPassword'), {
        description: t('resetPassword.resettingDescription'),
      });

      try {
        const captchaToken = await executeRecaptcha?.('reset_password');
        const response = await fetch(`${import.meta.env.VITE_ENDPOINT}/reset`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token,
            password: values.password,
            captchaToken,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || t('resetPassword.failedResetPassword'));
        }

        if (data.success) {
          analytics.capture('Password Reset Completed');
          setIsSubmitted(true);
          toast.success(t('resetPassword.passwordResetSuccess'), {
            description: t('resetPassword.passwordUpdated'),
            id: toastId,
          });
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : t('resetPassword.failedResetPassword');
        toast.error(t('resetPassword.failedResetPassword'), {
          description: errorMessage,
          id: toastId,
        });
      } finally {
        setIsLoading(false);
      }
    },
  });

  if (!token) {
    return (
      <div className={cn('flex flex-col gap-6', className)} {...props}>
        <Card className='overflow-hidden p-0'>
          <CardContent className='grid p-0 md:grid-cols-2'>
            <div className='p-6 md:p-8'>
              <div className='flex flex-col gap-6'>
                <div className='flex flex-col items-center text-center'>
                  <AuthLogo />
                  <h1 className='text-2xl font-bold'>{t('resetPassword.invalidResetLink')}</h1>
                  <p className='text-muted-foreground text-balance'>{t('resetPassword.invalidResetLinkDescription')}</p>
                </div>
                <div className='flex flex-col gap-4'>
                  <Button asChild className='w-full'>
                    <Link to='/auth/forgot'>{t('resetPassword.requestNewResetLink')}</Link>
                  </Button>
                  <Button asChild variant='ghost' className='w-full'>
                    <Link to='/auth/login'>{t('resetPassword.backToLogin')}</Link>
                  </Button>
                </div>
              </div>
            </div>
            <div className='bg-muted relative hidden md:block p-4'>
              <div className='absolute inset-0 p-4'>
                <img
                  src='/login.png'
                  alt='Image'
                  className='w-full h-full object-contain '
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className={cn('flex flex-col gap-6', className)} {...props}>
        <Card className='overflow-hidden p-0'>
          <CardContent className='grid p-0 md:grid-cols-2'>
            <div className='p-6 md:p-8'>
              <div className='flex flex-col gap-6'>
                <div className='flex flex-col items-center text-center'>
                  <AuthLogo />
                  <h1 className='text-2xl font-bold'>{t('resetPassword.resetSuccessful')}</h1>
                  <p className='text-muted-foreground text-balance'>{t('resetPassword.resetSuccessfulDescription')}</p>
                </div>
                <div className='flex flex-col gap-4'>
                  <Button asChild className='w-full'>
                    <Link to='/auth/login'>{t('resetPassword.continueToLogin')}</Link>
                  </Button>
                </div>
              </div>
            </div>
            <div className='bg-muted relative hidden md:block p-4'>
              <div className='absolute inset-0 p-4'>
                <img
                  src='/login.png'
                  alt='Image'
                  className='w-full h-full object-contain '
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card className='overflow-hidden p-0'>
        <CardContent className='grid p-0 md:grid-cols-2'>
          <form className='p-6 md:p-8' onSubmit={formik.handleSubmit}>
            <div className='flex flex-col gap-6'>
              <div className='flex flex-col items-center text-center'>
                <AuthLogo />
                <h1 className='text-2xl font-bold'>{t('resetPassword.title')}</h1>
                <p className='text-muted-foreground text-balance'>{t('resetPassword.description')}</p>
              </div>
              <InputWithError
                label={t('resetPassword.newPassword')}
                id='password'
                name='password'
                type='password'
                placeholder={t('resetPassword.newPasswordPlaceholder')}
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password ? formik.errors.password : undefined}
                touched={formik.touched.password}
                required
              />
              <InputWithError
                label={t('resetPassword.confirmPassword')}
                id='confirmPassword'
                name='confirmPassword'
                type='password'
                placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.confirmPassword ? formik.errors.confirmPassword : undefined}
                touched={formik.touched.confirmPassword}
                required
              />
              <Button type='submit' className='w-full' disabled={isLoading}>
                {isLoading ? t('resetPassword.resetting') : t('resetPassword.resetPassword')}
              </Button>
              <div className='text-center'>
                <Button asChild variant='ghost' className='text-sm'>
                  <Link to='/auth/login'>{t('resetPassword.backToLogin')}</Link>
                </Button>
              </div>
            </div>
          </form>
          <div className='bg-muted relative hidden md:block p-4'>
            <div className='absolute inset-0 p-4'>
              <img
                src='/login.png'
                alt='Image'
                className='w-full h-full object-contain '
              />
            </div>
          </div>
        </CardContent>
      </Card>
      <div className='text-muted-foreground *:[a]:hover:text-primary text-center text-xs text-balance *:[a]:underline *:[a]:underline-offset-4'>
        By clicking continue, you agree to our <a href='https://skygis.cloud/terms'>{t('login.termsOfService')}</a> and{' '}
        <a href='https://skygis.cloud/terms'>{t('login.privacyPolicy')}</a>.
      </div>
    </div>
  );
};
